import React from 'react';
import {
  TiptapEditor,
  TiptapEditorView,
  TiptapOutputType,
  Bold,
  Italic,
  Underline,
  BulletList,
  Code,
  CodeBlock,
  Newline,
  OrderedList,
  Redo,
  TextAlignCenter,
  TextAlignLeft,
  TextAlignRight,
  Undo,
  TiptapOutput,
  ClearFormatting,
  TiptapEditorHTMLView,
  SearchAndReplace,
  Link,
  CodeView,
} from '@amzn/awscat-editor';
import { InlineComment } from '@amzn/awscat-pattern-library-service-model';
import { isURL } from 'class-validator';
import { IMplServiceClient } from '../api/IMplServiceClient';
import { MplServiceClientFactory } from '../api/MplServiceClientFactory';
import FlashbarMessageBroker from '../common/FlashbarMessageBroker';
import { transformTiptapHtml } from '../api/ContentUtil';
import { withAppContext } from '../contexts/AppContext';
import { getUserRoles } from '../api/UtilsApi';
import AppContextProps from '../contexts/AppContextProps';
import { sanitizeCodeBlock, TiptapMenuType, fetchImageInBase64Format } from './utils';
import {
  ContentViewerContextData,
  withContentViewerContext,
} from '../components/workflow-viewer/ContentViewerContext';
import ErrorBoundary from './ErrorBoundary';

interface Props extends AppContextProps {
  readOnly: boolean;
  model: TiptapOutputType;
  onModelChange?: (input: TiptapOutputType) => void;
  contentViewerContext: ContentViewerContextData;
  id?: string;
  menuType?: TiptapMenuType;
  disabled?: boolean;
  removeFormatting?: boolean;
  singleLine?: boolean;
  hideMenuBar?: boolean;
  hideFooter?: boolean;
  autoFocus?: boolean;
}

interface State {
  richTextData: TiptapOutputType;
  isEditable: boolean;
  prevId?: string;
  prevDisabled?: boolean;
}
class EditorWrapper extends React.Component<Props, State> {
  private flashbarMessageBroker: FlashbarMessageBroker;
  private tiptapMenuItems: any[];
  private tiptapSimpleMenuItems: any[];
  private tiptapLinkOnlyMenuItem: any[];
  private readonly mplService: IMplServiceClient;
  // @ts-expect-error TS7006 - strict migration
  constructor(props) {
    super(props);
    this.flashbarMessageBroker = FlashbarMessageBroker.create();
    this.mplService = MplServiceClientFactory.create();
    this.tiptapMenuItems = [
      new Undo(),
      new Redo(),
      new Bold(),
      new Italic(),
      new Underline(),
      new Code(),
      new CodeBlock(),
      new Newline(),
      new TextAlignLeft(),
      new TextAlignCenter(),
      new TextAlignRight(),
      new OrderedList(),
      new BulletList(),
      new Link({
        beforeInsert: this.validateInsertedLink,
        validateLink: (link) => /(^https:\/\/)|(^www\.)/.test(link),
      }),
      new ClearFormatting(),
    ];
    this.tiptapSimpleMenuItems = [
      new Bold(),
      new Code(),
      new Link({
        beforeInsert: this.validateInsertedLink,
        validateLink: (link) => /(^https:\/\/)|(^www\.)/.test(link),
      }),
      new ClearFormatting(),
    ];

    this.tiptapLinkOnlyMenuItem = [
      new Link({
        beforeInsert: this.validateInsertedLink,
        validateLink: (link) => /(^https:\/\/)|(^www\.)/.test(link),
      }),
      new ClearFormatting(),
    ];

    // Enable Codeview editing for Admin/Techwriter users.
    const userProfile = this.props.appContext.authnInfo?.userProfile;
    if (userProfile) {
      const { isAdmin, isTechWriter } = getUserRoles(userProfile);
      if (isAdmin || isTechWriter) {
        this.tiptapMenuItems.push(new CodeView());
      }
    }
    this.tiptapMenuItems.push(new SearchAndReplace());

    const richTextData =
      typeof this.props.model === 'string' && !this.props.readOnly
        ? transformTiptapHtml(this.props.model)
        : this.props.model;
    this.state = {
      richTextData,
      isEditable: !this.props.disabled,
      prevId: this.props.id,
      prevDisabled: this.props.disabled,
    };
  }

  // Reset the editor's data when it switches to another item, and follow the disabled prop.
  public static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
    const isIdChanged = props.id !== state.prevId;
    const isDisabledChanged = props.disabled !== state.prevDisabled;
    if (!isIdChanged && !isDisabledChanged) {
      return null;
    }

    const richTextData =
      typeof props.model === 'string' && !props.readOnly
        ? transformTiptapHtml(props.model)
        : props.model;
    return {
      ...(isIdChanged && { richTextData }),
      ...(isDisabledChanged && { isEditable: !props.disabled }),
      prevId: props.id,
      prevDisabled: props.disabled,
    };
  }

  public shouldComponentUpdate(nextProps: any, nextState: any) {
    const contentChanged = this.props.onModelChange !== nextProps.onModelChange;
    const readonlyChange = this.props.readOnly !== nextProps.readOnly;
    return contentChanged || readonlyChange;
  }

  public render() {
    if (this.props.readOnly) {
      return this.renderTiptapEditorView();
    } else {
      return this.renderTiptapEditor();
    }
  }

  public getTaskComments = () => {
    const sectionComments = (this.props?.contentViewerContext?.inlineComments ?? []).filter(
      (comment) => comment.path[0] === 'epics' && comment.path?.[1] === this.props.id,
    );
    return sectionComments;
  };

  public renderTiptapEditor() {
    const { removeFormatting, singleLine, hideMenuBar, hideFooter } = this.props;
    const { entities } = this.props?.appContext ?? {};
    const { isEditable } = this.state;
    const comments = this.getTaskComments();
    const tiptapMenuItems = this.getTiptapMenuItems();

    const tiptapEditor = (
      <ErrorBoundary
        fallback={
          <TiptapEditor
            content={this.state.richTextData}
            menuItems={tiptapMenuItems}
            onChange={this.modelChange}
            outputFormats={['json', 'text', 'html']}
            comments={[]}
            // @ts-expect-error TS2322 - strict migration
            updateInlineCommentsLocation={this.updateCommentsLocation}
            // @ts-expect-error TS2322 - strict migration
            entities={entities}
            fetchImage={this.fetchImage}
            editable={isEditable}
            removeFormatting={!!removeFormatting}
            singleLine={!!singleLine}
            enableUniqueId={isEditable}
            hideMenuBar={hideMenuBar}
            hideFooter={hideFooter}
            autoFocus={this.props.autoFocus}
          />
        }
      >
        <TiptapEditor
          content={this.state.richTextData}
          menuItems={tiptapMenuItems}
          onChange={this.modelChange}
          outputFormats={['json', 'text', 'html']}
          comments={comments}
          // @ts-expect-error TS2322 - strict migration
          updateInlineCommentsLocation={this.updateCommentsLocation}
          // @ts-expect-error TS2322 - strict migration
          entities={entities}
          fetchImage={this.fetchImage}
          editable={isEditable}
          removeFormatting={!!removeFormatting}
          singleLine={!!singleLine}
          enableUniqueId={isEditable}
          hideMenuBar={hideMenuBar}
          hideFooter={hideFooter}
          autoFocus={this.props.autoFocus}
        />
      </ErrorBoundary>
    );
    return tiptapEditor;
  }

  public updateCommentsLocation = (comments: InlineComment[]) => {
    if (!comments.length) {
      return;
    }
    // @ts-expect-error TS2532 - strict migration
    const sectionId = comments[0].path[0] + comments[0].path?.[1];
    // @ts-expect-error TS2722 - strict migration
    this.props?.contentViewerContext?.updateInlineCommentsLocation(comments, sectionId);
  };

  public renderTiptapEditorView() {
    const { entities } = this.props?.appContext ?? {};
    return typeof this.props.model === 'string' ? (
      <TiptapEditorHTMLView model={this.props.model} />
    ) : (
      <ErrorBoundary
        fallback={
          <TiptapEditorView
            model={sanitizeCodeBlock(this.props.model)}
            // @ts-expect-error TS2322 - strict migration
            entities={entities}
            fetchImage={this.fetchImage}
          />
        }
      >
        <TiptapEditorView
          model={sanitizeCodeBlock(this.props.model)}
          // @ts-expect-error TS2322 - strict migration
          entities={entities}
          fetchImage={this.fetchImage}
        />
      </ErrorBoundary>
    );
  }

  private getTiptapMenuItems = () => {
    switch (this.props.menuType) {
      case TiptapMenuType.Hide:
        return [];
      case TiptapMenuType.Simple:
        return this.tiptapSimpleMenuItems;
      case TiptapMenuType.OnlyLink:
        return this.tiptapLinkOnlyMenuItem;
      default:
        return this.tiptapMenuItems;
    }
  };

  private modelChange = (editor: any, output: TiptapOutput) => {
    const sanitizedCodeBlock = sanitizeCodeBlock(output.json);
    const { onModelChange } = this.props;
    if (onModelChange) {
      onModelChange(sanitizedCodeBlock);
    }
  };

  // Validate link before insert
  private validateInsertedLink = async (link: string) => {
    if (link.startsWith('/') || link.startsWith('#')) {
      return true;
    }

    // Validate link to enforce valid URL format and HTTPS
    if (!isURL(link) || !link.toLowerCase().startsWith('https://')) {
      this.flashbarMessageBroker.pushErrorMessage(
        'Could not insert link. Your URL is invalid or not using HTTPS.',
      );
      return false;
    }
    return true;
  };

  private fetchImage = async (srcUrl: string) => {
    return await fetchImageInBase64Format(srcUrl, this.mplService);
  };
}

const WrappedEditorWrapper = withContentViewerContext(withAppContext(EditorWrapper));
export default WrappedEditorWrapper;
